// src/features/goal/pages/GoalDetailPage.tsx
// Goal 詳細頁面 - 顯示 Goal 資訊、活躍度熱力圖、Task 列表
//
// 路由：/goals/:id
// 從 Goals 列表卡片點擊導航進入

import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useHeader } from '../../../hooks/useHeader';
import { useGoalDetail } from '../hooks/useGoalDetail';
import GoalBreakdownPanel from '../components/GoalBreakdownPanel';
import GoalActivityHeatmap from '../components/GoalActivityHeatmap';
import GoalTaskList from '../components/GoalTaskList';

const GoalDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    objective,
    objectivesLoading,
    currentUser,
    tasks,
    tasksLoading,
    showBreakdownPanel,
    breakdownPanelRef,
    loadTasks,
    handleToggleTaskStatus,
    handleTaskAdded,
    handleTaskAddFailed,
    handleTaskCreated,
    handleAddTask,
    openBreakdownPanel,
    dismissBreakdownPanel,
  } = useGoalDetail(id);

  // 配置 Header
  const headerConfig = useMemo(() => ({
    title: objective?.title ?? 'Goal',
    leftAction: (
      <button
        onClick={() => navigate('/goals')}
        className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Back to Goals"
      >
        <ArrowLeft size={20} className="text-gray-600" />
      </button>
    ),
  }), [objective?.title, navigate]);

  useHeader(headerConfig);

  // Loading 狀態
  if (objectivesLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (!objective) return null;

  return (
    <div className="content-standard space-y-4">
      {/* 活躍度熱力圖 */}
      <GoalActivityHeatmap tasks={tasks} />

      {/* AI 拆解建議面板 */}
      {showBreakdownPanel && currentUser && (
        <div ref={breakdownPanelRef}>
          <GoalBreakdownPanel
            objectiveId={objective.id}
            userId={currentUser.uid}
            onTaskAdded={handleTaskAdded}
            onTaskAddFailed={handleTaskAddFailed}
            onTaskCreated={handleTaskCreated}
            onDismiss={dismissBreakdownPanel}
          />
        </div>
      )}

      {/* Task 列表 */}
      <GoalTaskList
        tasks={tasks}
        tasksLoading={tasksLoading}
        onToggleStatus={handleToggleTaskStatus}
        onRefresh={loadTasks}
        onAddTask={handleAddTask}
        onBreakdown={openBreakdownPanel}
        breakdownActive={showBreakdownPanel}
      />
    </div>
  );
};

export default GoalDetailPage;
